import { format } from 'date-fns';
import type { QaServedModelAttempt } from './types';

export type RangeKey = '24h' | '7d' | '30d';
export type IntervalKey = '15m' | '1h' | '6h' | '1d';

export const RANGE_MS: Record<RangeKey, number> = {
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
};

export const INTERVAL_MS: Record<IntervalKey, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
};

export const DEFAULT_INTERVAL: Record<RangeKey, IntervalKey> = {
  '24h': '1h',
  '7d': '6h',
  '30d': '1d',
};

export interface TimeBucket {
  t: number;
  label: string;
  ok: number;
  error: number;
  total: number;
  rps: number;
  p50: number | null;
  p95: number | null;
  concurrent: number;
}

function percentile(sortedAsc: number[], p: number): number | null {
  if (sortedAsc.length === 0) return null;
  const idx = Math.min(
    Math.max(Math.ceil(p * sortedAsc.length) - 1, 0),
    sortedAsc.length - 1,
  );
  return sortedAsc[idx];
}

function attemptTime(attempt: QaServedModelAttempt): number | null {
  if (!attempt.started_at) return null;
  const ts = new Date(attempt.started_at).getTime();
  return Number.isNaN(ts) ? null : ts;
}

export function bucketAttempts(
  attempts: QaServedModelAttempt[],
  rangeKey: RangeKey,
  interval: IntervalKey,
  now = Date.now(),
): TimeBucket[] {
  const step = INTERVAL_MS[interval];
  const end = now;
  const start = Math.floor((end - RANGE_MS[rangeKey]) / step) * step;
  const labelFmt =
    step >= INTERVAL_MS['1d'] ? 'MMM d' : rangeKey === '24h' ? 'HH:mm' : 'M/d HH:mm';
  const seconds = step / 1000;

  const counts = new Map<
    number,
    { ok: number; error: number; completed: number; durations: number[] }
  >();
  for (let t = start; t <= end; t += step) {
    counts.set(t, { ok: 0, error: 0, completed: 0, durations: [] });
  }

  const events: { t: number; delta: number }[] = [];
  for (const attempt of attempts) {
    const ts = attemptTime(attempt);
    if (ts == null) continue;
    const bucket = Math.floor(ts / step) * step;
    const entry = counts.get(bucket);
    if (entry) {
      if (attempt.ok) {
        entry.ok += 1;
        if (attempt.duration_seconds != null) entry.durations.push(attempt.duration_seconds);
      } else {
        entry.error += 1;
      }
    }
    const durationMs = Math.max((attempt.duration_seconds ?? 0) * 1000, 0);
    const completedBucket = Math.floor((ts + durationMs) / step) * step;
    const completedEntry = counts.get(completedBucket);
    if (completedEntry) completedEntry.completed += 1;
    events.push({ t: ts, delta: 1 });
    events.push({ t: ts + durationMs, delta: -1 });
  }
  events.sort((a, b) => a.t - b.t || a.delta - b.delta);

  let current = 0;
  let eventIndex = 0;
  const concurrent = new Map<number, number>();
  for (let t = start; t <= end; t += step) {
    while (eventIndex < events.length && events[eventIndex].t < t) {
      current += events[eventIndex].delta;
      eventIndex += 1;
    }
    let peak = Math.max(current, 0);
    while (eventIndex < events.length && events[eventIndex].t < t + step) {
      current += events[eventIndex].delta;
      peak = Math.max(peak, current);
      eventIndex += 1;
    }
    concurrent.set(t, peak);
  }

  return Array.from(counts.entries()).map(([t, v]) => {
    const durations = [...v.durations].sort((a, b) => a - b);
    const total = v.ok + v.error;
    return {
      t,
      label: format(new Date(t), labelFmt),
      ok: v.ok,
      error: v.error,
      total,
      rps: v.completed / seconds,
      p50: percentile(durations, 0.5),
      p95: percentile(durations, 0.95),
      concurrent: concurrent.get(t) ?? 0,
    };
  });
}

export function recentErrors(
  attempts: QaServedModelAttempt[],
  limit = 20,
): QaServedModelAttempt[] {
  return [...attempts]
    .filter((attempt) => !attempt.ok)
    .sort((a, b) => (attemptTime(b) ?? 0) - (attemptTime(a) ?? 0))
    .slice(0, limit);
}

export function slowestRequests(
  attempts: QaServedModelAttempt[],
  limit = 10,
): QaServedModelAttempt[] {
  return [...attempts]
    .filter((attempt) => attempt.ok && attempt.duration_seconds != null)
    .sort((a, b) => (b.duration_seconds as number) - (a.duration_seconds as number))
    .slice(0, limit);
}
